import React, { useState } from 'react';
import apiClient from '../../utils/apiClient';
import { classesFilter, teamsFilter, positionsFilter, MBB_POSITION, MBB_TEAMS } from '../../utils/constant';
import {
    ChevronDown,
    ChevronUp,
    Brain,
    RefreshCw,
    LineChart,
    Eye,
    EyeClosed,
    Loader,
    XCircle

} from 'lucide-react';
import ViewEvaluationModal from './ViewEvaluationModal';
import { EvaluationQueryModal, MultipleEvaluationQueryModal } from './EvaluationQueryModal';
import PlayerDetails from './PlayerDetails';
import SlamEvaluationResult from './SlamEvaluationResult';
import TransferPortalFilters from './TransferPortalFilters';

const TransferPortalPlayersList = ({
    filteredPlayers,
    expandedItems,
    togglePlayerAccordion,
    onEvaluateClick,
    onViewEvaluation,
    loadingPlayerDetails,
    loadingEvaluations,
    playerDetails,
    playerEvaluations,
    savedEvaluations,
    setPlayerEvaluations,
    saveEvaluation,
    savingStatus,
    sortField,
    sortDirection,
    handleSort,
    selectedSchema,
    DEFAULT_QUERY,
    runPlayerEvaluation,
}) => {
    // Store complete player objects instead of just booleans
    const [selectedPlayers, setSelectedPlayers] = useState({});
    const [isEvalLoading, setIsEvalLoading] = useState(false)
    const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);

    // Handler for selecting/deselecting a single player
    const handlePlayerSelect = (player) => {
        setSelectedPlayers(prev => {
            const newSelected = { ...prev };
            if (newSelected[player.player_id]) {
                // If already selected, remove it
                delete newSelected[player.player_id];
            } else {
                // If not selected, add the complete player object
                newSelected[player.player_id] = player;
            }
            return newSelected;
        });
    };

    // Handler for selecting/deselecting all players
    const handleSelectAll = () => {
        if (Object.keys(selectedPlayers).length === filteredPlayers.length) {
            // If all are selected, deselect all
            setSelectedPlayers({});
        } else {
            // Otherwise, select all
            const allSelected = {};
            filteredPlayers.forEach(player => {
                allSelected[player.player_id] = player;
            });
            setSelectedPlayers(allSelected);
        }
    };

    // Check if all players are selected
    const allSelected = filteredPlayers.length > 0 &&
        Object.keys(selectedPlayers).length === filteredPlayers.length;

    // Count selected players
    const selectedCount = Object.keys(selectedPlayers)?.length;

    // Function to evaluate all selected players
    const evaluateSelectedPlayers = async (query) => {
        handleCloseQueryModal()
        // Get array of selected player objects
        setIsEvalLoading(true)
        const playersToEvaluate = Object.values(selectedPlayers);

        // Evaluate each player sequentially
        for (const player of playersToEvaluate) {
            await runPlayerEvaluation(query, player.player_id);
        }
        setSelectedPlayers({})
        setIsEvalLoading(false)
    };

    const handleStartBulkEvaluation = () => {
        setIsQueryModalOpen(true);
    };

    // Handle closing the query modal
    const handleCloseQueryModal = () => {
        setIsQueryModalOpen(false);
    };

    return (
        <div>
            {/* Bulk evaluation button - only show when players are selected */}
            {selectedCount > 0 && (
                <div className="mb-4 bg-[#1A1A1A] p-3 rounded-[14px] flex items-center justify-between border border-[rgba(232,184,0,0.2)]">
                    <span className="text-sm text-[#E8B800] font-medium">{selectedCount} player{selectedCount !== 1 ? 's' : ''} selected</span>
                    <button
                        onClick={handleStartBulkEvaluation}
                        disabled={isEvalLoading}
                        className={`inline-flex items-center px-4 py-2 rounded-[14px] text-sm font-medium ${isEvalLoading
                            ? 'bg-[#E8B800] text-black cursor-not-allowed opacity-80'
                            : 'bg-[#E8B800] text-black hover:bg-[#d4a800]'
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E8B800] transition-colors`}
                    >
                        {isEvalLoading ? (
                            <>
                                <Loader size={16} className="mr-2 animate-spin text-black" />
                                Evaluating...
                            </>
                        ) : (
                            <>
                                <Brain size={16} className="mr-2 text-black" />
                                Evaluate Selected Players
                            </>
                        )}
                    </button>
                </div>
            )}
            <div className="overflow-x-auto" style={{ maxWidth: '100%', width: '100%' }}>
                <table className="w-full min-w-[900px] table-auto">
                    <thead
                        style={{
                            background: 'linear-gradient(135deg, rgba(232,184,0,0.08) 0%, rgba(232,184,0,0.04) 100%)',
                            borderBottom: '1px solid rgba(232,184,0,0.15)',
                        }}
                    >
                        <tr>
                            {/* New checkbox column */}
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-[#F5F5F5] uppercase tracking-wider">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 text-[#E8B800] focus:ring-[#E8B800] border-[#555555] rounded"
                                    checked={allSelected}
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                onClick={() => handleSort('player_name')}
                            >
                                <div className="flex items-center">
                                    <span className={sortField === 'player_name' ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}>Player</span>
                                    <div className="ml-1">
                                        {sortField === 'player_name' ? (
                                            sortDirection === 'asc' ?
                                                <ChevronUp size={14} className="text-[#E8B800]" /> :
                                                <ChevronDown size={14} className="text-[#E8B800]" />
                                        ) : (
                                            <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                        )}
                                    </div>
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                onClick={() => handleSort('position')}
                            >
                                <div className="flex items-center">
                                    <span className={sortField === 'position' ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}>Position</span>
                                    <div className="ml-1">
                                        {sortField === 'position' ? (
                                            sortDirection === 'asc' ?
                                                <ChevronUp size={14} className="text-[#E8B800]" /> :
                                                <ChevronDown size={14} className="text-[#E8B800]" />
                                        ) : (
                                            <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                        )}
                                    </div>
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                onClick={() => handleSort('class')}
                            >
                                <div className="flex items-center">
                                    <span className={sortField === 'class' ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}>Class</span>
                                    <div className="ml-1">
                                        {sortField === 'class' ? (
                                            sortDirection === 'asc' ?
                                                <ChevronUp size={14} className="text-[#E8B800]" /> :
                                                <ChevronDown size={14} className="text-[#E8B800]" />
                                        ) : (
                                            <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                        )}
                                    </div>
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                onClick={() => handleSort('latest_team')}
                            >
                                <div className="flex items-center">
                                    <span className={sortField === 'latest_team' ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}>Latest Team</span>
                                    <div className="ml-1">
                                        {sortField === 'latest_team' ? (
                                            sortDirection === 'asc' ?
                                                <ChevronUp size={14} className="text-[#E8B800]" /> :
                                                <ChevronDown size={14} className="text-[#E8B800]" />
                                        ) : (
                                            <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                        )}
                                    </div>
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                onClick={() => handleSort('destination_team')}
                            >
                                <div className="flex items-center">
                                    <span className={sortField === 'destination_team' ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}>Destination</span>
                                    <div className="ml-1">
                                        {sortField === 'destination_team' ? (
                                            sortDirection === 'asc' ?
                                                <ChevronUp size={14} className="text-[#E8B800]" /> :
                                                <ChevronDown size={14} className="text-[#E8B800]" />
                                        ) : (
                                            <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                        )}
                                    </div>
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                onClick={() => handleSort('date_entered')}
                            >
                                <div className="flex items-center">
                                    <span className={sortField === 'date_entered' ? 'text-[#E8B800]' : 'text-[#F5F5F5] text-nowrap'}>Date Entered</span>
                                    <div className="ml-1">
                                        {sortField === 'date_entered' ? (
                                            sortDirection === 'asc' ?
                                                <ChevronUp size={14} className="text-[#999999]" /> :
                                                <ChevronDown size={14} className="text-[#999999]" />
                                        ) : (
                                            <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                        )}
                                    </div>
                                </div>
                            </th>
                            <th
                                scope="col"
                                className="px-3 py-3 text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                onClick={() => handleSort('SLAM_SCORE')}
                            >
                                <div className="flex items-center">
                                    <span className={`text-nowrap ${sortField === 'SLAM_SCORE' ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}`}>
                                        Slam Score
                                    </span>
                                    <div className="ml-1">
                                        {sortField === 'SLAM_SCORE' ? (
                                            sortDirection === 'asc' ?
                                                <ChevronUp size={14} className="text-[#E8B800]" /> :
                                                <ChevronDown size={14} className="text-[#E8B800]" />
                                        ) : (
                                            <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                        )}
                                    </div>
                                </div>
                            </th>
                            {selectedSchema === 'WBB' && (
                                <th
                                    scope="col"
                                    className="px-3 py-3 text-xs font-semibold uppercase tracking-wider cursor-pointer group hover:bg-[rgba(255,255,255,0.04)]"
                                    onClick={() => handleSort('nil_value')}
                                >
                                    <div className="flex items-center">
                                        <span className={sortField === 'nil_value' ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}>NIL Value</span>
                                        <div className="ml-1">
                                            {sortField === 'nil_value' ? (
                                                sortDirection === 'asc' ?
                                                    <ChevronUp size={14} className="text-[#999999]" /> :
                                                    <ChevronDown size={14} className="text-[#999999]" />
                                            ) : (
                                                <ChevronDown size={14} className="text-[#999999] opacity-0 group-hover:opacity-100" />
                                            )}
                                        </div>
                                    </div>
                                </th>
                            )}
                            <th scope="col" className="px-3 py-3 text-center text-xs font-semibold text-[#F5F5F5] uppercase tracking-wider whitespace-nowrap">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        style={{
                            background: 'transparent',
                        }}
                    >
                        {filteredPlayers.length > 0 ? (
                            filteredPlayers.map((player, index) => (
                                <React.Fragment key={player.player_id}>
                                    <tr
                                        className="group cursor-pointer"
                                        style={{
                                            transition: 'all 0.2s ease',
                                            background: selectedPlayers[player.player_id]
                                                ? 'linear-gradient(135deg, rgba(232,184,0,0.10) 0%, rgba(232,184,0,0.04) 100%)'
                                                : expandedItems[player.player_id]
                                                    ? 'linear-gradient(135deg, rgba(232,184,0,0.06) 0%, rgba(232,184,0,0.02) 100%)'
                                                    : 'transparent',
                                            borderBottom: '1px solid rgba(255,255,255,0.04)',
                                            outline: selectedPlayers[player.player_id] ? '2px solid #E8B800' : 'none',
                                            outlineOffset: '-2px',
                                            borderRadius: selectedPlayers[player.player_id] ? '4px' : '0',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!expandedItems[player.player_id] && !selectedPlayers[player.player_id]) {
                                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!expandedItems[player.player_id] && !selectedPlayers[player.player_id]) {
                                                e.currentTarget.style.background = 'transparent';
                                            }
                                        }}
                                    >
                                        {/* New checkbox cell */}
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 text-[#E8B800] focus:ring-[#E8B800] border-[#555555] rounded"
                                                checked={!!selectedPlayers[player.player_id]}
                                                onChange={() => handlePlayerSelect(player)}
                                            />
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap ">
                                            <div className="flex items-center">
                                                {player.image_url?.length && (
                                                    <img
                                                        src={player.image_url}
                                                        alt={player.latest_team}
                                                        className="h-8 w-8 mr-3"
                                                    />
                                                )}
                                                <div className="font-medium text-[#F5F5F5]">
                                                    {player.player_name}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            <span
                                                className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-lg"
                                                style={{
                                                    background: 'linear-gradient(135deg, #E8B800 0%, #D4A800 100%)',
                                                    color: '#000',
                                                    boxShadow: '0 2px 8px rgba(232,184,0,0.3)',
                                                }}
                                            >
                                                {player.position}
                                            </span>
                                        </td>
                                        <td className={`px-3 py-3 whitespace-nowrap text-sm text-[#999999]`}>
                                            {player.class}
                                        </td>
                                        <td className={`px-3 py-3 whitespace-nowrap text-sm text-[#999999]`}>
                                            {player.latest_team}
                                        </td>
                                        <td className={`px-3 py-3 whitespace-nowrap text-sm text-[#999999]`}>
                                            {player.destination_team !== '--' ? player.destination_team : 'Undecided'}
                                        </td>
                                        <td className={`px-3 py-3 whitespace-nowrap text-sm text-[#999999]`}>
                                            {player.date_entered ? new Date(player.date_entered).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div
                                                    className="w-full max-w-[80px] h-2 mr-3 rounded-full overflow-hidden"
                                                    style={{
                                                        background: 'rgba(255,255,255,0.1)',
                                                    }}
                                                >
                                                    <div
                                                        className="h-full rounded-full transition-all duration-500"
                                                        style={{
                                                            width: `${player?.SLAM_SCORE || 0}%`,
                                                            background: (player?.SLAM_SCORE || 0) >= 80
                                                                ? 'linear-gradient(135deg, #E8B800 0%, #D4A800 100%)'
                                                                : (player?.SLAM_SCORE || 0) >= 60
                                                                    ? 'linear-gradient(135deg, #D4A800 0%, #FF8C00 100%)'
                                                                    : (player?.SLAM_SCORE || 0) >= 40
                                                                        ? 'linear-gradient(135deg, #FF8C00 0%, #FF6B00 100%)'
                                                                        : 'linear-gradient(135deg, #FF6B6B 0%, #FF4757 100%)',
                                                            boxShadow: (player?.SLAM_SCORE || 0) >= 80
                                                                ? '0 0 8px rgba(232,184,0,0.5)'
                                                                : '0 0 8px rgba(232,184,0,0.3)',
                                                        }}
                                                    />
                                                </div>
                                                <span
                                                    className="text-sm font-bold"
                                                    style={{
                                                        color: (player?.SLAM_SCORE || 0) >= 80
                                                            ? '#E8B800'
                                                            : '#F5F5F5',
                                                    }}
                                                >
                                                    {player?.SLAM_SCORE || 0}
                                                </span>
                                            </div>
                                        </td>
                                        {selectedSchema === 'WBB' && (
                                            <td className="px-3 py-3 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#E8B800] text-black">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                    </svg>
                                                    {player.nil_value}
                                                </span>
                                            </td>
                                        )}
                                        <td className={`px-3 py-4 whitespace-nowrap text-sm font-medium flex space-x-2 `}>
                                            <button
                                                className="hover:text-[#E8B800]"
                                                onClick={() => togglePlayerAccordion(player, 'eyeButton')}
                                            >
                                                {expandedItems[player.player_id] ? (
                                                    <EyeClosed className="inline-block w-4 h-4" aria-label="Hide Details" />
                                                ) : (
                                                    <Eye className="inline-block w-4 h-4" aria-label="View Details" />
                                                )}
                                            </button>

                                            {/* SLAM Eval Button */}
                                            <button
                                                className="ml-2 inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300"
                                                style={{
                                                    background: loadingEvaluations[player.player_id]
                                                        ? 'rgba(232,184,0,0.2)'
                                                        : expandedItems[`eval-${player.player_id}`]
                                                            ? 'linear-gradient(135deg, #E8B800 0%, #D4A800 100%)'
                                                            : 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(232,184,0,0.1) 100%)',
                                                    border: expandedItems[`eval-${player.player_id}`]
                                                        ? '1px solid rgba(232,184,0,0.5)'
                                                        : '1px solid rgba(232,184,0,0.3)',
                                                    boxShadow: expandedItems[`eval-${player.player_id}`]
                                                        ? '0 4px 16px rgba(232,184,0,0.3)'
                                                        : '0 2px 8px rgba(232,184,0,0.15)',
                                                    color: expandedItems[`eval-${player.player_id}`]
                                                        ? '#000'
                                                        : '#E8B800',
                                                    cursor: loadingEvaluations[player.player_id] ? 'not-allowed' : 'pointer',
                                                    opacity: loadingEvaluations[player.player_id] ? 0.7 : 1,
                                                }}
                                                onClick={() => {
                                                    if (loadingEvaluations[player.player_id]) return;

                                                    if (playerEvaluations[player.player_id]) {
                                                        // Toggle accordion for inline view
                                                        togglePlayerAccordion(`eval-${player.player_id}`);
                                                    } else if (savedEvaluations[player.player_id]) {
                                                        // If we have a saved evaluation but not a loaded one
                                                        // Load the saved evaluation data if needed
                                                        try {
                                                            console.log('Loading saved evaluation for player:', player.player_id);
                                                            console.log('Saved data:', savedEvaluations[player.player_id]);

                                                            // Make sure the saved data has the expected structure
                                                            if (savedEvaluations[player.player_id].agent_output &&
                                                                typeof savedEvaluations[player.player_id].agent_output.output === 'string') {

                                                                const savedData = JSON.parse(savedEvaluations[player.player_id].agent_output.output);
                                                                console.log('Successfully parsed saved data:', savedData);

                                                                setPlayerEvaluations(prev => ({
                                                                    ...prev,
                                                                    [player.player_id]: savedData
                                                                }));
                                                                togglePlayerAccordion(`eval-${player.player_id}`);
                                                            } else {
                                                                console.error("Saved evaluation has unexpected structure", savedEvaluations[player.player_id]);
                                                                onEvaluateClick(player.player_id);
                                                            }
                                                        } catch (err) {
                                                            console.error("Error parsing saved evaluation", err);
                                                            onEvaluateClick(player.player_id);
                                                        }
                                                    } else {
                                                        // Pass to runPlayerEvaluation via another function
                                                        onEvaluateClick(player.player_id);
                                                    }
                                                }}
                                                disabled={loadingEvaluations[player.player_id]}
                                            >
                                                {loadingEvaluations[player.player_id] ? (
                                                    <>
                                                        <div className="w-4 h-4 border-t-2 border-b-2 border-[#E8B800] rounded-full animate-spin mr-2"></div>
                                                        Running...
                                                    </>
                                                ) : expandedItems[`eval-${player.player_id}`] ? (
                                                    <>
                                                        <LineChart size={16} className="text-[#E8B800] mr-1" />
                                                        View SLAM Eval
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* <Brain size={16} className="text-[#E8B800] mr-1" /> */}
                                                        SLAM Eval
                                                    </>
                                                )}
                                            </button>

                                            {/* View Evaluation Modal Button - Only shown if there's a saved evaluation */}
                                            {savedEvaluations[player.player_id] && (
                                                <button
                                                    className="ml-2 inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200"
                                                    style={{
                                                        background: 'rgba(255,255,255,0.05)',
                                                        border: '1px solid rgba(232,184,0,0.3)',
                                                        color: '#E8B800',
                                                    }}
                                                    onClick={() => onViewEvaluation(player.player_id, player.player_name)}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.background = 'rgba(232,184,0,0.15)';
                                                        e.currentTarget.style.borderColor = 'rgba(232,184,0,0.5)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                                        e.currentTarget.style.borderColor = 'rgba(232,184,0,0.3)';
                                                    }}
                                                >
                                                    <Eye size={16} className="mr-1" />
                                                    View Full Eval
                                                </button>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Expanded Player Details */}
                                    {expandedItems[player.player_id] && (
                                        <tr>
                                            <td
                                                colSpan={selectedSchema === 'WBB' ? 9 : 12}
                                                className="px-8 py-4"
                                                style={{ backgroundColor: '#0A0A0A' }}
                                            >
                                                <PlayerDetails
                                                    player={player}
                                                    loadingDetails={loadingPlayerDetails[player.player_id]}
                                                    details={playerDetails[player.player_id]}
                                                    selectedSchema={selectedSchema}
                                                />
                                            </td>
                                        </tr>
                                    )}

                                    {/* SLAM Evaluation Section */}
                                    {expandedItems[`eval-${player.player_id}`] && (
                                        <tr>
                                            <td
                                                colSpan={selectedSchema === 'WBB' ? 9 : 8}
                                                className="px-3 py-3"
                                                style={{ backgroundColor: '#0A0A0A' }}
                                            >
                                                <div className="border-t pt-4">
                                                    <div className="flex items-center mb-3">
                                                        <LineChart size={20} className="text-[#E8B800] mr-2" />
                                                        <h3 className="text-lg font-medium text-[#F5F5F5]">SLAM AI Evaluation</h3>
                                                    </div>
                                                    <SlamEvaluationResult
                                                        evaluation={playerEvaluations[player.player_id]}
                                                        isLoading={loadingEvaluations[player.player_id]}
                                                        onSave={() => saveEvaluation(player.player_id, playerEvaluations[player.player_id])}
                                                        saveStatus={savingStatus[player.player_id]}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={selectedSchema === 'WBB' ? 9 : 8} className={`px-3 py-3 text-center `}>
                                    No transfer portal players match your filters
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <MultipleEvaluationQueryModal defaultQuery={DEFAULT_QUERY} isOpen={isQueryModalOpen} onClose={handleCloseQueryModal} evaluateSelectedPlayers={evaluateSelectedPlayers} />
        </div>
    );
};

const TransferPortalTab = ({
    transferPortalPlayers,
    transferPortalLoading,
    selectedSchema,
    onSchemaChange,
    savedEvaluations,
    setSortField = () => { },
    sortField = '',
    setSortDirection = () => { },
    sortDirection = '',
    totalPlayers,
    setFilters = () => { },
    searchTerm = '',
    setSearchTerm = () => { },
    filters = {
        position: [],
        class: [],
        min_possessions: null,
        latestTeam: [],
        destination: [],
        slamScore: [0, 100] // Default SLAM score range [min, max]
    },
    setSavedEvaluations
}) => {
    const [expandedItems, setExpandedItems] = useState({});
    const [playerDetails, setPlayerDetails] = useState({});
    const [loadingPlayerDetails, setLoadingPlayerDetails] = useState({});
    const [playerEvaluations, setPlayerEvaluations] = useState({});
    const [loadingEvaluations, setLoadingEvaluations] = useState({});
    const [savingStatus, setSavingStatus] = useState({});
    const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
    const [viewEvaluationModalOpen, setViewEvaluationModalOpen] = useState(false);
    const [selectedPlayerId, setSelectedPlayerId] = useState(null);
    const [selectedPlayerName, setSelectedPlayerName] = useState('');
    const DEFAULT_QUERY = selectedSchema == 'MBB' ? "Provide a position-specific evaluation using available data only. Guards: assess ball-handling, shooting efficiency, assist-to-turnover ratio, and defensive pressure. Forwards: evaluate rebounding rates, scoring versatility, mid-range shooting. Centers: analyze rim protection, post scoring, defensive rebounding, and paint presence. Include player strengths, development areas, and overall impact. Do not highlight missing data; only provide \"Data Gaps\" at the end. You must always provide a player fit score." :
        "Evaluate this player based on their position and on the data that is available. Do not highlight missing data; only provide \"Data Gaps\" at the end. Also, if the player is a non-pitcher, do not provide pitcher evaluation and vice versa. You must always provide a player fit score.";
    // Extract unique values for filter dropdowns
    const positions = selectedSchema == 'MBB' ? MBB_POSITION : positionsFilter
    const classes = classesFilter
    const latestTeams = /* selectedSchema == 'MBB' ? MBB_TEAMS : */ teamsFilter
    const destinations = teamsFilter

    // Filter players based on selected filters and search term
    const filteredPlayers = transferPortalPlayers


    // Handle filter changes
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => {
            // Special case for slamScore which is an array of [min, max]
            if (filterName === 'slamScore') {
                return {
                    ...prev,
                    slamScore: value
                };
            }

            // If value is an array (e.g. [] for "All" or full replacement), set it directly
            if (Array.isArray(value)) {
                return {
                    ...prev,
                    [filterName]: value
                };
            }

            // If value is a single item, toggle it
            if (filterName === 'min_possessions') {
                return {
                    ...prev,
                    [filterName]: value
                };
            }

            // Make sure prev[filterName] is an array
            const currentValue = Array.isArray(prev[filterName]) ? prev[filterName] : [];

            if (currentValue.includes(value)) {
                return {
                    ...prev,
                    [filterName]: currentValue.filter(item => item !== value)
                };
            } else {
                return {
                    ...prev,
                    [filterName]: [...currentValue, value]
                };
            }
        });
    };

    // Handle removing a single filter
    const removeFilter = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: prev[filterName].filter(item => item !== value)
        }));
    };

    // Reset all filters
    const resetFilters = () => {
        setFilters({
            position: [],
            class: [],
            latestTeam: [],
            min_possessions: null,
            destination: [],
            slamScore: [0, 100]
        });
        setSearchTerm('');
    };

    // Handle sorting when a column header is clicked
    const handleSort = (field) => {
        setSortDirection(currentField => {
            // If changing fields, always start with ascending sort
            if (sortField !== field) {
                return 'asc';
            }
            const cond = currentField === 'asc' ? 'desc' : 'asc'
            // If clicking the same field, toggle direction
            return cond
        });
        setSortField(field);
    };

    const fetchPlayerDetails = async (playerId) => {
        if (playerDetails[playerId]) return;

        setLoadingPlayerDetails(prev => ({
            ...prev,
            [playerId]: true
        }));

        try {
            const response = await apiClient.get(`https://slam-all-python-359065791766.us-central1.run.app/CBB/transfer-portal/players/${playerId}?schema=${selectedSchema}`);
            setPlayerDetails(prev => ({
                ...prev,
                [playerId]: response.data
            }));
        } catch (error) {
            console.error(`Error fetching details for player ${playerId}:`, error);
        } finally {
            setLoadingPlayerDetails(prev => ({
                ...prev,
                [playerId]: false
            }));
        }
    };

    const fetchMBBPlayerDetails = async (player) => {
        console.log('player', player)
        const playerId = player?.player_id
        if (playerDetails[playerId]) return;

        setLoadingPlayerDetails(prev => ({
            ...prev,
            [playerId]: true
        }));

        try {
            if (selectedSchema == 'MBB') {
                // const sport = selectedSchema
                // const baseUrl = `https://slam-all-python-359065791766.us-central1.run.app/${sport}/players-stats-mia/search-by-player?schema=${sport}`;

                // const response = await apiClient.post(baseUrl, {
                //     player_name: player?.player_name
                // });

                // const statsData = response?.data?.data?.[0];
                // console.log('statsData', statsData)
                const statsData = { ...player }
                setPlayerDetails(prev => ({
                    ...prev,
                    [playerId]: { ...statsData, position: player?.position, date_entered: player?.date_entered, destination_team: player?.destination_team }
                }));
            }
            else {
                const response = await apiClient.get(`https://slam-all-python-359065791766.us-central1.run.app/CBB/transfer-portal/players/${playerId}?schema=${selectedSchema}`);
                setPlayerDetails(prev => ({
                    ...prev,
                    [playerId]: response.data
                }));
            }
        } catch (error) {
            console.error(`Error fetching details for player ${playerId}:`, error);
        } finally {
            setLoadingPlayerDetails(prev => ({
                ...prev,
                [playerId]: false
            }));
        }
    };

    // Check for existing saved evaluation
    const checkSavedEvaluation = async (playerId) => {
        try {
            const response = await apiClient.get(`https://slam-all-python-359065791766.us-central1.run.app/${selectedSchema}/transfer-portal/tp-evaluation/${playerId}?schema=${selectedSchema}`);
            if (response.data) {
                setSavedEvaluations(prev => ({
                    ...prev,
                    [playerId]: response.data
                }));
                return true;
            }
        } catch (error) {
            console.error(`Error checking saved evaluation for player ${playerId}:`, error);
        }
        return false;
    };

    // Generate a UUID v4 for unique session IDs
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0,
                v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // Save player evaluation
    const saveEvaluation = async (playerId, evaluation, isShowAlert = true) => {
        if (!playerId || !evaluation) return;

        // Set saving status
        setSavingStatus(prev => ({
            ...prev,
            [playerId]: 'saving'
        }));

        try {
            // Generate a proper UUID for the session_id
            const sessionId = generateUUID();

            const playerName = transferPortalPlayers.find(p => p.player_id === playerId)?.player_name || 'Unknown Player';

            const payload = {
                player_id: playerId,
                session_id: sessionId,
                agent_output: {
                    name: playerName,
                    Schema: selectedSchema,
                    output: JSON.stringify(evaluation)
                }
            };

            await apiClient.post(`https://slam-all-python-359065791766.us-central1.run.app/${selectedSchema}/transfer-portal/tp-evaluation?schema=${selectedSchema}`, payload);

            setSavingStatus(prev => ({
                ...prev,
                [playerId]: 'saved'
            }));

            // Update saved evaluations
            await checkSavedEvaluation(playerId);

            // Display success message as an alert
            isShowAlert && alert(`Evaluation for ${playerName} has been saved successfully with session ID: ${sessionId}`);

            // Auto-hide the saved status after 3 seconds
            setTimeout(() => {
                setSavingStatus(prev => ({
                    ...prev,
                    [playerId]: 'initial'
                }));
            }, 3000);

        } catch (error) {
            console.error(`Error saving evaluation for player ${playerId}:`, error);
            setSavingStatus(prev => ({
                ...prev,
                [playerId]: 'error'
            }));

            // Show error alert
            alert(`Error saving evaluation: ${error.message || 'Unknown error'}`);

            // Auto-hide the error status after 3 seconds
            setTimeout(() => {
                setSavingStatus(prev => ({
                    ...prev,
                    [playerId]: 'initial'
                }));
            }, 3000);
        }
    };

    // Open evaluation modal
    const openEvaluationModal = async (playerId) => {
        setSelectedPlayerId(playerId);

        // Check if there's a saved evaluation
        const hasSaved = await checkSavedEvaluation(playerId);
        if (hasSaved) {
            // Inform user there's a saved evaluation they might want to view
            const confirmOverwrite = window.confirm(
                "This player already has a saved evaluation. Do you want to create a new one? Click OK to create a new evaluation or Cancel to view the saved one."
            );

            if (!confirmOverwrite) {
                // If they choose to view saved, load it and toggle view
                setExpandedItems(prev => ({
                    ...prev,
                    [`eval-${playerId}`]: true
                }));
                return;
            }
        }

        // Open modal for new evaluation
        setEvaluationModalOpen(true);
    };

    // Open view evaluation modal
    const openViewEvaluationModal = (playerId, playerName) => {
        setSelectedPlayerId(playerId);
        setSelectedPlayerName(playerName);

        // Debug log the saved evaluation data to help troubleshoot parsing issues
        if (savedEvaluations[playerId]) {
            console.log('Opening view modal with data:', savedEvaluations[playerId]);

            // Try to parse the output to check its structure
            try {
                // Check if we have the expected TP evaluation format
                if (savedEvaluations[playerId].output &&
                    typeof savedEvaluations[playerId].output === 'string') {
                    console.log('Found direct output property, trying to parse');
                    const parsed = JSON.parse(savedEvaluations[playerId].output);
                    console.log('Successfully parsed output:', parsed);
                }
                // Check for agent_output format
                else if (savedEvaluations[playerId].agent_output &&
                    typeof savedEvaluations[playerId].agent_output.output === 'string') {
                    console.log('Found agent_output.output property, trying to parse');
                    const parsed = JSON.parse(savedEvaluations[playerId].agent_output.output);
                    console.log('Successfully parsed agent_output.output:', parsed);
                }
            } catch (error) {
                console.error('Error parsing saved evaluation output:', error);
            }
        } else {
            console.warn('No saved evaluation found for player ID:', playerId);
        }

        setViewEvaluationModalOpen(true);
    };

    // Run player evaluation with custom query
    const runPlayerEvaluation = async (customQuery = DEFAULT_QUERY, player_id = null) => {
        if (!selectedPlayerId && !player_id) return;

        const playerId = player_id ? player_id : selectedPlayerId;

        // If already evaluated and they're just viewing it again
        if (playerEvaluations[playerId] && !evaluationModalOpen) {
            // Just toggle the expansion
            setExpandedItems(prev => ({
                ...prev,
                [`eval-${playerId}`]: !prev[`eval-${playerId}`]
            }));
            return;
        }

        setLoadingEvaluations(prev => ({
            ...prev,
            [playerId]: true
        }));

        try {
            // Close modal if open
            setEvaluationModalOpen(false);

            const response = await apiClient.post('https://slam-adk-359065791766.us-central1.run.app/player-eval-agent', {
                roster_id: playerId,
                query: customQuery,
                sports: selectedSchema
            });

            setPlayerEvaluations(prev => ({
                ...prev,
                [playerId]: response.data
            }));

            // Auto-expand the evaluation section
            setExpandedItems(prev => ({
                ...prev,
                [`eval-${playerId}`]: true
            }));

            // Auto-save the evaluation
            saveEvaluation(playerId, response.data, false);

        } catch (error) {
            console.error(`Error evaluating player ${playerId}:`, error);
        } finally {
            setLoadingEvaluations(prev => ({
                ...prev,
                [playerId]: false
            }));
        }
    };

    const toggleItem = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const togglePlayerAccordion = (playerId, action = '') => {
        // Toggle the expanded state for this ID
        if (action == 'eyeButton') {
            ; const player_id = playerId?.player_id
            setExpandedItems(prev => ({
                ...prev,
                [player_id]: !prev[player_id]
            }));
            // If this is a player details request (not an eval- prefix) and we're expanding
            if (!expandedItems[player_id]) {
                fetchMBBPlayerDetails(playerId);
            }
        }
        else {
            setExpandedItems(prev => ({
                ...prev,
                [playerId]: !prev[playerId]
            }));

            // If this is a player details request (not an eval- prefix) and we're expanding
            if (!playerId.startsWith('eval-') && !expandedItems[playerId]) {
                fetchPlayerDetails(playerId);
            }
        }
    };

    return (
        <div
            className="rounded-[14px] overflow-hidden w-full max-w-full"
            style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.06)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.03)',
            }}
        >
            <div
                className="px-6 py-5"
                style={{
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                }}
            >
                <div className="flex justify-between space-x-8 items-center">
                    {/* Glass Search Input */}
                    <div className="w-full max-w-xl">
                        <div className="relative">
                            <input
                                type="text"
                                id="search"
                                className="w-full pl-10 pr-10 py-2.5 rounded-[14px] text-sm transition-all duration-300 focus:outline-none"
                                style={{
                                    height: '42px',
                                    background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2), 0 1px 0 rgba(255,255,255,0.05)',
                                    color: '#F5F5F5',
                                }}
                                placeholder="Player or team name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(232,184,0,0.5)';
                                    e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0,0,0,0.2), 0 0 0 3px rgba(232,184,0,0.1)';
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                                    e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0,0,0,0.2), 0 1px 0 rgba(255,255,255,0.05)';
                                }}
                            />
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#999999]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#999999] hover:text-[#F5F5F5] transition-colors"
                                >
                                    <XCircle size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex space-x-3">
                        {/* Glass Sport Selector */}
                        <select
                            className="rounded-[14px] text-sm font-medium cursor-pointer transition-all duration-300 focus:outline-none"
                            style={{
                                padding: '10px 36px 10px 16px',
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                                color: '#F5F5F5',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 12px center',
                            }}
                            value={selectedSchema}
                            onChange={(e) => onSchemaChange(e.target.value)}
                        >
                            {selectedSchema === 'CBB' ? (
                                <option value="CBB">College Baseball</option>
                            ) : (
                                <>
                                    <option value="MBB">Men's Basketball</option>
                                    <option value="WBB">Women's Basketball</option>
                                </>
                            )}
                        </select>
                        {/* Gradient Refresh Button */}
                        <button
                            className="inline-flex items-center px-4 py-2.5 text-sm font-semibold rounded-[14px] transition-all duration-300"
                            style={{
                                background: 'linear-gradient(135deg, #E8B800 0%, #D4A800 100%)',
                                border: '1px solid rgba(232,184,0,0.3)',
                                boxShadow: '0 4px 20px rgba(232,184,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                                color: '#000',
                            }}
                            onClick={() => onSchemaChange(selectedSchema)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 6px 24px rgba(232,184,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 20px rgba(232,184,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)';
                            }}
                        >
                            <RefreshCw size={16} className="mr-2" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div>
                {transferPortalLoading ? (
                    <div className="flex justify-center items-center p-12">
                        <div className="w-12 h-12 border-t-4 border-b-4 border-[#E8B800] rounded-full animate-spin"></div>
                        <p className="ml-3 text-lg text-[#555555]">Loading players...</p>
                    </div>
                ) : (
                    <div>
                        <TransferPortalFilters
                            selectedSchema={selectedSchema}
                            positions={positions}
                            classes={classes}
                            latestTeams={latestTeams}
                            destinations={destinations}
                            filters={filters}
                            handleFilterChange={handleFilterChange}
                            removeFilter={removeFilter}
                            // searchTerm={searchTerm}
                            // setSearchTerm={setSearchTerm}
                            resetFilters={resetFilters}
                        />

                        <div
                            className="px-3 py-3 flex items-center justify-between"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                                borderBottom: '1px solid rgba(255,255,255,0.06)'
                            }}
                        >
                            <span
                                className="text-sm font-medium"
                                style={{
                                    background: 'linear-gradient(135deg, #E8B800 0%, #D4A800 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {totalPlayers || filteredPlayers.length} players found
                            </span>
                        </div>

                        <TransferPortalPlayersList
                            DEFAULT_QUERY={DEFAULT_QUERY}
                            filteredPlayers={filteredPlayers}
                            expandedItems={expandedItems}
                            togglePlayerAccordion={togglePlayerAccordion}
                            onEvaluateClick={openEvaluationModal}
                            onViewEvaluation={openViewEvaluationModal}
                            loadingPlayerDetails={loadingPlayerDetails}
                            loadingEvaluations={loadingEvaluations}
                            playerDetails={playerDetails}
                            playerEvaluations={playerEvaluations}
                            savedEvaluations={savedEvaluations}
                            setPlayerEvaluations={setPlayerEvaluations}
                            saveEvaluation={saveEvaluation}
                            savingStatus={savingStatus}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            handleSort={handleSort}
                            selectedSchema={selectedSchema}
                            runPlayerEvaluation={runPlayerEvaluation}
                        />
                    </div>
                )}
            </div>

            {/* Evaluation Query Modal */}
            <EvaluationQueryModal
                isOpen={evaluationModalOpen}
                onClose={() => setEvaluationModalOpen(false)}
                playerId={selectedPlayerId}
                onSubmit={runPlayerEvaluation}
                defaultQuery={DEFAULT_QUERY}
                isLoading={selectedPlayerId ? loadingEvaluations[selectedPlayerId] : false}
            />

            {/* View Evaluation Modal */}
            <ViewEvaluationModal
                isOpen={viewEvaluationModalOpen}
                onClose={() => setViewEvaluationModalOpen(false)}
                playerName={selectedPlayerName}
                evaluationData={selectedPlayerId ? (
                    // If we have a player ID and saved evaluation data
                    savedEvaluations[selectedPlayerId] ? (
                        // Return the saved evaluation, mapping based on the known API format
                        savedEvaluations[selectedPlayerId].agent_output ?
                            savedEvaluations[selectedPlayerId].agent_output :
                            savedEvaluations[selectedPlayerId]
                    ) : null
                ) : null}
                onRunNew={() => {
                    setViewEvaluationModalOpen(false);
                    openEvaluationModal(selectedPlayerId);
                }}
            />
        </div>
    );
};

export default TransferPortalTab;